import { db } from '../firebase';
import { fetchPublicProfiles, chunkIds } from './publicProfilesBatch';
import { throwIfOfflineEmpty } from './recoverableLoad';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  documentId
} from 'firebase/firestore';
import { doesPedalMatchWant } from './matchingLogic';
import { sortPedalsByListedAt } from './sortPedalsByListedAt';

const conditionRanking = {
  'Damaged': 1, 'Poor': 2, 'Good': 3,
  'Very Good': 4, 'Excellent': 5, 'New': 6
};

// The one filter block both loaders share: availability + the want-matching
// rules the dashboard/matches UI has always applied (identity via
// doesPedalMatchWant, plus maxPrice / condition-floor / decade — NOT color;
// see TECH_DEBT.md R3 for the color-semantics note vs the server predicate).
function pedalPassesWantFilters(pedal, userWants, userId, minPrice, maxPrice, hasPriceWindow) {
  if (pedal.ownerId === userId || pedal.inTrade || pedal.hiddenFromPedalboard) return false;

  if (hasPriceWindow) {
    const pedalPrice = Number(pedal.price || 0);
    if (pedalPrice < minPrice || pedalPrice > maxPrice) return false;
  }

  for (const want of userWants) {
    const pedalMatches = doesPedalMatchWant(want, pedal);
    const matchesPrice = !want.maxPrice || pedal.price <= want.maxPrice;

    const wantConditionRank = conditionRanking[want.condition] || 0;
    const pedalConditionRank = conditionRanking[pedal.condition] || 0;
    const conditionOk = !want.condition || pedalConditionRank >= wantConditionRank;

    const decadeOk = !want.decade || !want.decade.trim() || pedal.decade === want.decade;

    if (pedalMatches && matchesPrice && conditionOk && decadeOk) return true;
  }
  return false;
}

async function enrichWithOwners(matchedPedals) {
  // Batched documentId()-in reads (one round trip per 30 owners). The same
  // normalization applies to found profiles and missing ones ({} normalizes
  // to the exact fallback the per-owner version produced).
  const ownerIds = [...new Set(matchedPedals.map(p => p.ownerId))];
  const rawProfiles = await fetchPublicProfiles(ownerIds, { fallback: {} });
  const userData = {};
  for (const ownerId of ownerIds) {
    const data = rawProfiles[ownerId] || {};
    userData[ownerId] = {
      username: data.username || 'User',
      completedTrades: data.completedTrades || 0,
      photoURL: data.photoURL || null,
      averageRating: data.averageRating || null,
      membershipTier: data.membershipTier || null,
      lastActiveWeek: data.lastActiveWeek || null,
      lastActiveAt: data.lastActiveAt || null,
      responderBadge: data.responderBadge || null
    };
  }

  return matchedPedals.map(pedal => ({
    ...pedal,
    ownerPhotoURL: (userData[pedal.ownerId] || {}).photoURL,
    owner: {
      username: (userData[pedal.ownerId] || {}).username || 'User',
      badge: (userData[pedal.ownerId] || {}).completedTrades || 0,
      averageRating: (userData[pedal.ownerId] || {}).averageRating,
      membershipTier: (userData[pedal.ownerId] || {}).membershipTier,
      lastActiveWeek: (userData[pedal.ownerId] || {}).lastActiveWeek,
      lastActiveAt: (userData[pedal.ownerId] || {}).lastActiveAt,
      responderBadge: (userData[pedal.ownerId] || {}).responderBadge || null
    }
  }));
}

/**
 * Get pedals that match the user's Wantlist
 * @param {string} userId - Current user's ID
 * @param {number|null} limit - Max matches to return (null = no limit)
 * @param {object} [options]
 * @param {number} [options.minPrice] - Only include listings at/above this price
 * @param {number} [options.maxPrice] - Only include listings at/below this price
 * @param {boolean} [options.skipOwnerEnrichment] - Skip publicProfiles reads (lighter for pickers)
 * @returns {Promise<Array>} - Array of pedals with optional owner data
 */
export async function getPedalsMatchingWantList(userId, limit = 8, options = {}) {
  const {
    minPrice = null,
    maxPrice = null,
    skipOwnerEnrichment = false,
  } = options || {};

  const hasPriceWindow =
    minPrice != null && maxPrice != null && Number.isFinite(minPrice) && Number.isFinite(maxPrice);

  try {
    
    // Get user's Wantlist
    const wantsSnapshot = await getDocs(
      query(collection(db, 'wantList'), where('uid', '==', userId))
    );
    const userWants = wantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    
    if (userWants.length === 0) {
      return [];
    }
    
    // Fetch active listings newest-first by listedAt (composite index
    // active + listedAt DESC); the price window (bulk-send modal) is applied
    // inside the shared filter. NOTE: this is the O(catalog) scan — the
    // dashboard carousel uses getWantListMatchesFromServer below instead
    // (TECH_DEBT.md R3); this function remains for WantListMatchesPage and
    // the OfferPage bulk-send modal until they graduate.
    const listingsSnapshot = await getDocs(
      query(
        collection(db, 'listings'),
        where('active', '==', true),
        orderBy('listedAt', 'desc')
      )
    );

    // Iterate newest-first; stopping at `limit` avoids extra want-matching once enough match.
    const matchedPedals = [];

    for (const docSnap of listingsSnapshot.docs) {
      if (limit && matchedPedals.length >= limit) break;

      const pedal = { id: docSnap.id, ...docSnap.data() };
      if (pedalPassesWantFilters(pedal, userWants, userId, minPrice, maxPrice, hasPriceWindow)) {
        matchedPedals.push(pedal);
      }
    }

    if (skipOwnerEnrichment) {
      return limit ? matchedPedals : sortPedalsByListedAt(matchedPedals);
    }

    const enrichedPedals = await enrichWithOwners(matchedPedals);

    // When fetching with a limit the results are already newest-first from Firestore.
    // For the full page (no limit) we still sort client-side via listedAt priority.
    return limit ? enrichedPedals : sortPedalsByListedAt(enrichedPedals);
  } catch (error) {
    console.error('❌ Error fetching Wantlist matches:', error);
    return [];
  }
}

/**
 * Server-backed replacement for the dashboard carousel (TECH_DEBT.md R3).
 *
 * Instead of downloading every active listing and filtering client-side
 * (O(catalog) reads per dashboard visit), this reads the server-maintained
 * `wantsMyPedals` rows for this user (written by functions/matchMaker.js on
 * every want/listing write, pruned by the cleanup* functions) and hydrates
 * only those listings — O(my matches) reads. The same query shape already
 * runs in production for the dashboard "X new" counter
 * (dashboardMatchCounters.js), so rules and index are proven.
 *
 * The SAME client-side filter block is re-applied to the hydrated listings
 * to guard against stale rows (listing deactivated / went into a trade /
 * hidden, want edited since the row was written). Semantics note: the server
 * predicate also enforces the want's color, which this UI never has — so
 * this can only ever show a subset of the old scan where wants specify a
 * color (quantified by functions/scripts/audit-wantlist-matches-parity.js
 * before this shipped; see TECH_DEBT.md R3).
 *
 * Same signature, options, and output shape as getPedalsMatchingWantList.
 * Failure contract preserved: any error logs and returns [].
 */
export async function getWantListMatchesFromServer(userId, limit = 8, options = {}) {
  const {
    minPrice = null,
    maxPrice = null,
    skipOwnerEnrichment = false,
  } = options || {};

  const hasPriceWindow =
    minPrice != null && maxPrice != null && Number.isFinite(minPrice) && Number.isFinite(maxPrice);

  try {
    // The user's wants drive the re-filter; no wants means no matches by
    // definition, so skip the wantsMyPedals query entirely.
    const wantsSnapshot = await getDocs(
      query(collection(db, 'wantList'), where('uid', '==', userId))
    );
    // Offline, these queries RESOLVE with empty cache-served snapshots (no
    // persistent cache) instead of rejecting — which used to render as a
    // legitimate "No matches yet" (Phase 2c, 2026-09-29). Unconfirmed
    // results throw; server-confirmed empties still return [] below.
    throwIfOfflineEmpty(wantsSnapshot, 'wantListMatches:wants');
    const userWants = wantsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    if (userWants.length === 0) {
      return [];
    }

    const wmpSnapshot = await getDocs(
      query(collection(db, 'wantsMyPedals'), where('wantingUserId', '==', userId))
    );
    throwIfOfflineEmpty(wmpSnapshot, 'wantListMatches:wmp');
    if (wmpSnapshot.empty) {
      return [];
    }

    // Multiple wants can match the same listing — hydrate each listing once.
    const pedalIds = [...new Set(wmpSnapshot.docs.map(d => d.data().pedalId).filter(Boolean))];

    // Chunked documentId()-in hydration, one round trip per 30 listings
    // (same ceiling/pattern as publicProfilesBatch).
    const chunks = chunkIds(pedalIds);
    const snapshots = await Promise.all(
      chunks.map(chunk =>
        getDocs(query(collection(db, 'listings'), where(documentId(), 'in', chunk)))
      )
    );

    const matchedPedals = [];
    for (const snap of snapshots) {
      snap.forEach(docSnap => {
        const pedal = { id: docSnap.id, ...docSnap.data() };
        if (!pedal.active) return; // wantsMyPedals rows only ever point at active listings; stale rows are pruned server-side, but re-check
        if (pedalPassesWantFilters(pedal, userWants, userId, minPrice, maxPrice, hasPriceWindow)) {
          matchedPedals.push(pedal);
        }
      });
    }

    const sorted = sortPedalsByListedAt(matchedPedals);
    const limited = limit ? sorted.slice(0, limit) : sorted;

    if (skipOwnerEnrichment) {
      return limited;
    }
    return await enrichWithOwners(limited);
  } catch (error) {
    // The offline false-empty must reach the caller so a failed load can be
    // told apart from a genuinely empty result; every caller of this helper
    // already catches and degrades, and the dashboard now shows its
    // couldn't-load state instead of a false "No matches yet".
    if (error?.name === 'OfflineResultError') throw error;
    console.error('❌ Error fetching server-backed Wantlist matches:', error);
    return [];
  }
}
